import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { editDishReducer } from './editDishReducer';
import { EditDishActionTypes } from './editDishActions';
import {
	TFileId,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	TLanguageIDs,
	IDefaultLanguage,
	ITotalPrice,
	IGetDishInfo,
	IItemForGetShortDishInfo,
} from 'types';
import {
	EditDishContext,
	editDishInitialState,
	IEditDishErrorResponseData,
	TEditDishVisibleForm,
	TTypeOfEditDishModalWindow,
	IIDsShowInComboDishes,
} from './editDishContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getDishInfo } from 'backend/dishes';
import DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';

const EditDishState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editDishReducer, editDishInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const dishID = params.get('dishID');

	const { response, loading, refetch } = useFetchData<IGetDishInfo, string>(
		dishID ? getDishInfo : null,
		dishID || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditDishActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useNamedHeader(response?.data.dish.name);

	useEffect(() => {
		if (response) {
			const IDsShowInComboDishes: IIDsShowInComboDishes = {};
			response.data.dish.dishModifications.forEach((el) => {
				IDsShowInComboDishes[el.modificationId] = el.showInCombo;
			});

			dispatch({
				type: EditDishActionTypes.SET_ALL_DATA,
				payload: {
					filePath: response.data.dish.filePath,
					preSignedUrl: response.data.dish.preSignedUrl,
					smallFilePath: response.data.dish.smallFilePath,
					smallPreSignedUrl: response.data.dish.smallPreSignedUrl,
					videoPath: response.data.dish.videoPath,
					videoPreSignedUrl: response.data.dish.videoPreSignedUrl,
					isVideoActive: !!response.data.dish.videoPath,
					dishId: response.data.dish.id,
					name: response.data.dish.name || '',
					selectedCustomer: response.data.dish.customer?.id || '',
					articleNumber: response.data.dish.articleNumber,
					restaurantsForPrice: response.data.dish.restaurantsDishes.map((el) => el.restaurantId),
					totalPrices: response.data.dish.restaurantsDishes.map((el) => ({
						restaurantId: el.restaurantId,
						restaurantName: el.restaurantName,
						restaurantStatus: el.restaurantStatus,
						price: String(el.price),
					})),
					restaurantWithTurnOffComment: response.data.dish.restaurantsDishes
						.filter((el) => el.isCommentTurnOff)
						.map((el) => el.restaurantId),
					restaurantWithRequiredComment: response.data.dish.restaurantsDishes
						.filter((el) => el.isCommentRequired)
						.map((el) => el.restaurantId),
					shortDescription: response.data.dish.shortDescription
						? DOMPurify.sanitize(response.data.dish.shortDescription)
						: '',
					posName: response.data.dish.posName,
					isComboDish: response.data.dish.isCombo,
					initialSelectedComboModifier: response.data.dish.comboModifierId,
					initialCategories: response.data.dish.categories,
					initialSubCategories: response.data.dish.allSubCategories,
					externalId: response.data.dish.externalId,
					isCreatedInPos: response.data.dish.isCreatedInPos,
					isDisabledInRestaurants: response.data.dish.restaurantsDishes
						.filter((el) => el.isDisabled)
						.map((el) => el.restaurantId),
					activationSchedule: response.data.dish.activationCron || '',
					deactivationSchedule: response.data.dish.deactivationCron || '',
					initialModifierGroups: response.data.dish.modifierGroups,
					modifierGroupsWithAdditionalData: response.data.dish.modifierGroups.map((el) => ({
						modifierGroupId: el.modifierGroupId,
						lowerLimit: el.downLimit != null ? String(el.downLimit) : '',
						upperLimit: el.upLimit != null ? String(el.upLimit) : '',
						isLimitsProtectedFromSync: el.isLimitsProtectedFromSync,
					})),
					initialModifierGroupsAsAdditional: response.data.dish.modifierGroups
						.filter((el) => el.isAdditional)
						.map((el) => el.modifierGroupId),
					selectedSuggestedDishes: response.data.dish.suggestedDishes.map((el) => el.suggestedId),
					fetchedSuggestedDishes: response.data.dish.suggestedDishes.map((el) => ({
						id: el.suggestedId,
						name: el.suggestedName,
						externalId: el.externalId,
					})),
					selectedAdditionalDishes: response.data.dish.additionalDishes.map((el) => el.additionalId),
					fetchedAdditionalDishes: response.data.dish.additionalDishes.map((el) => ({
						id: el.additionalId,
						name: el.additionalName,
						externalId: el.externalId,
					})),
					selectedDishModifications: response.data.dish.dishModifications.map((el) => el.modificationId),
					IDsShowInComboDishes,
					fetchedDishModifications: response.data.dish.dishModifications.map((el) => ({
						id: el.modificationId,
						name: el.modificationName,
						externalId: el.externalId,
					})),
					translations: response.data.dish.translations,
					isTaxPercentProtectedFromSync: response.data.dish.isTaxPercentProtectedFromSync,
					taxPercent: response.data.dish.taxPercent,
					hasOpenPrice: response.data.dish.hasOpenPrice,
					openPrice: response.data.dish.openPrice,
					openPriceMaxThreshold: response.data.dish.openPriceMaxThreshold,
					isFiscalized: response.data.dish.isFiscalized,
				},
			});
			dispatch({
				type: EditDishActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						name: response.data.dish.name || '',
						selectedCustomer: response.data.dish.customer?.id || '',
						totalPrices: response.data.dish.restaurantsDishes.map((el) => ({
							restaurantId: el.restaurantId,
							restaurantName: el.restaurantName,
							restaurantStatus: el.restaurantStatus,
							price: String(el.price),
						})),
						restaurantWithTurnOffComment: response.data.dish.restaurantsDishes
							.filter((el) => el.isCommentTurnOff)
							.map((el) => el.restaurantId),
						restaurantWithRequiredComment: response.data.dish.restaurantsDishes
							.filter((el) => el.isCommentRequired)
							.map((el) => el.restaurantId),
						shortDescription: response.data.dish.shortDescription
							? DOMPurify.sanitize(response.data.dish.shortDescription)
							: '',
						posName: response.data.dish.posName,
						selectedComboModifier: response.data.dish.comboModifierId,
						externalId: response.data.dish.externalId,
						isDisabledInRestaurants: response.data.dish.restaurantsDishes
							.filter((el) => el.isDisabled)
							.map((el) => el.restaurantId),
						activationSchedule: response.data.dish.activationCron || '',
						deactivationSchedule: response.data.dish.deactivationCron || '',
						selectedModifierGroups: response.data.dish.modifierGroups.map((el) => el.modifierGroupId),
						modifierGroupsWithAdditionalData: response.data.dish.modifierGroups.map((el) => ({
							modifierGroupId: el.modifierGroupId,
							lowerLimit: el.downLimit ? String(el.downLimit) : '',
							upperLimit: el.upLimit ? String(el.upLimit) : '',
							isLimitsProtectedFromSync: el.isLimitsProtectedFromSync,
						})),
						selectedModifierGroupsAsAdditional: response.data.dish.modifierGroups
							.filter((el) => el.isAdditional)
							.map((el) => el.modifierGroupId),
						selectedSuggestedDishes: response.data.dish.suggestedDishes.map((el) => el.suggestedId),
						selectedAdditionalDishes: response.data.dish.additionalDishes.map((el) => el.additionalId),
						selectedDishModifications: response.data.dish.dishModifications.map((el) => el.modificationId),
						IDsShowInComboDishes,
						filePath: response.data.dish.filePath,
						smallFilePath: response.data.dish.smallFilePath,
						videoPath: response.data.dish.videoPath,
						translations: response.data.dish.translations,
						isTaxPercentProtectedFromSync: response.data.dish.isTaxPercentProtectedFromSync,
						taxPercent: response.data.dish.taxPercent,
						hasOpenPrice: response.data.dish.hasOpenPrice,
						openPrice: response.data.dish.openPrice,
						openPriceMaxThreshold: response.data.dish.openPriceMaxThreshold,
						isFiscalized: response.data.dish.isFiscalized,
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditDishActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TEditDishVisibleForm) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditDishActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditDishActionTypes.DELETE_PHOTO }), []);

	const setSmallFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditDishActionTypes.SET_SMALL_FILE_ID,
			payload: {
				smallFileId: value,
			},
		});
	}, []);

	const deleteSmallPhoto = useCallback(() => dispatch({ type: EditDishActionTypes.DELETE_SMALL_PHOTO }), []);

	const setVideoFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditDishActionTypes.SET_VIDEO_FILE_ID,
			payload: {
				videoFileId: value,
			},
		});
	}, []);

	const deleteVideo = useCallback(() => dispatch({ type: EditDishActionTypes.DELETE_VIDEO }), []);

	const setIsVideoActive = useCallback((value: boolean) => {
		dispatch({
			type: EditDishActionTypes.SET_IS_VIDEO_ACTIVE,
			payload: {
				isVideoActive: value,
			},
		});
	}, []);

	const changeDishName = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_DISH_NAME,
			payload: {
				name: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfoWithStatus[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setRestaurantsForPrice = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_RESTAURANTS_FOR_PRICE,
			payload: {
				restaurantsForPrice: value,
			},
		});
	}, []);

	const changePrice = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_PRICE,
			payload: {
				price: value,
			},
		});
	}, []);

	const addPrices = useCallback((value: ITotalPrice[]) => {
		dispatch({
			type: EditDishActionTypes.ADD_PRICE,
			payload: {
				totalPrices: value,
			},
		});
	}, []);

	const removePrice = useCallback(
		(value: string[]) => {
			const updatedTotalPrices = state.totalPrices.filter((el) => !value.includes(el.restaurantId));
			const updatedRestaurantsForPrices = state.restaurantsForPrice.filter((el) => !value.includes(el));
			const updatedIsDisabledInRestaurants = state.isDisabledInRestaurants.filter((el) => !value.includes(el));
			const updatedRestaurantWithTurnOffComment = state.restaurantWithTurnOffComment.filter(
				(el) => !value.includes(el),
			);
			const updatedRestaurantWithRequiredComment = state.restaurantWithRequiredComment.filter(
				(el) => !value.includes(el),
			);
			dispatch({
				type: EditDishActionTypes.REMOVE_PRICE,
				payload: {
					totalPrices: updatedTotalPrices,
					restaurantsForPrice: updatedRestaurantsForPrices,
					isDisabledInRestaurants: updatedIsDisabledInRestaurants,
					restaurantWithTurnOffComment: updatedRestaurantWithTurnOffComment,
					restaurantWithRequiredComment: updatedRestaurantWithRequiredComment,
				},
			});
		},
		[
			state.totalPrices,
			state.restaurantsForPrice,
			state.isDisabledInRestaurants,
			state.restaurantWithTurnOffComment,
			state.restaurantWithRequiredComment,
		],
	);

	const setRestaurantWithTurnOffComment = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_RESTAURANT_WITH_TURN_OFF_COMMENT,
			payload: {
				restaurantWithTurnOffComment: value,
			},
		});
	}, []);

	const setRestaurantWithRequiredComment = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_RESTAURANT_WITH_REQUIRED_COMMENT,
			payload: {
				restaurantWithRequiredComment: value,
			},
		});
	}, []);

	const changeShortDescription = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_DISH_DESCRIPTION,
			payload: {
				shortDescription: value,
			},
		});
	}, []);

	const setFetchedComboModifier = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_COMBO_MODIFIER,
			payload: {
				fetchedComboModifier: value,
			},
		});
	}, []);

	const setComboModifier = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_ID_COMBO_MODIFIER,
			payload: {
				selectedComboModifier: value,
			},
		});
	}, []);

	const updateComboModifier = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfComboModifierShortInfo = value.map((el) => el.id);
			const selectedComboModifier =
				state.selectedComboModifier && IDsOfComboModifierShortInfo.includes(state.selectedComboModifier)
					? state.selectedComboModifier
					: null;

			dispatch({
				type: EditDishActionTypes.SET_ID_COMBO_MODIFIER,
				payload: {
					selectedComboModifier,
				},
			});
		},
		[state.selectedComboModifier],
	);

	const setDisabledInRestaurants = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_DISH_IS_DISABLED,
			payload: {
				isDisabledInRestaurants: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditDishActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditDishActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: EditDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedModifierGroups = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_MODIFIER_GROUPS,
			payload: {
				fetchedModifierGroups: value,
			},
		});
	}, []);

	const setModifierGroups = useCallback(
		(value: string[]) => {
			const filteredSelectedModifierGroupsAsAdditional = state.selectedModifierGroupsAsAdditional.filter((el) =>
				value.includes(el),
			);
			const modifierGroupsWithAdditionalData = value.map((el) => {
				const groupFromPreviousState = state.modifierGroupsWithAdditionalData.find(
					(exist) => exist.modifierGroupId === el,
				);
				const newGroup = {
					modifierGroupId: el,
					upperLimit: '',
					lowerLimit: '',
					isLimitsProtectedFromSync: false,
				};
				return groupFromPreviousState ? groupFromPreviousState : newGroup;
			});
			dispatch({
				type: EditDishActionTypes.SET_MODIFIER_GROUPS,
				payload: {
					selectedModifierGroups: value,
					modifierGroupsWithAdditionalData,
					selectedModifierGroupsAsAdditional: filteredSelectedModifierGroupsAsAdditional,
				},
			});
		},
		[state.selectedModifierGroupsAsAdditional, state.modifierGroupsWithAdditionalData],
	);

	const changeLowerLimit = useCallback(
		(id: string, value: string) => {
			const updatedModifierGroupsWithAdditionalData = state.modifierGroupsWithAdditionalData.map((el) =>
				el.modifierGroupId === id ? { ...el, lowerLimit: value } : el,
			);

			dispatch({
				type: EditDishActionTypes.UPDATE_MODIFIER_GROUPS,
				payload: {
					modifierGroupsWithAdditionalData: updatedModifierGroupsWithAdditionalData,
				},
			});
		},
		[state.modifierGroupsWithAdditionalData],
	);

	const changeUpperLimit = useCallback(
		(id: string, value: string) => {
			const updatedModifierGroupsWithAdditionalData = state.modifierGroupsWithAdditionalData.map((el) =>
				el.modifierGroupId === id ? { ...el, upperLimit: value } : el,
			);

			dispatch({
				type: EditDishActionTypes.UPDATE_MODIFIER_GROUPS,
				payload: {
					modifierGroupsWithAdditionalData: updatedModifierGroupsWithAdditionalData,
				},
			});
		},
		[state.modifierGroupsWithAdditionalData],
	);

	const toggleLimitsProtectedFromSync = useCallback(
		(id: string, value: boolean) => {
			const updatedModifierGroupsWithAdditionalData = state.modifierGroupsWithAdditionalData.map((el) =>
				el.modifierGroupId === id ? { ...el, isLimitsProtectedFromSync: value } : el,
			);

			dispatch({
				type: EditDishActionTypes.UPDATE_MODIFIER_GROUPS,
				payload: {
					modifierGroupsWithAdditionalData: updatedModifierGroupsWithAdditionalData,
				},
			});
		},
		[state.modifierGroupsWithAdditionalData],
	);

	const setModifierGroupsAsAdditional = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_MODIFIER_GROUPS_AS_ADDITIONAL,
			payload: {
				selectedModifierGroupsAsAdditional: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setSuggestedDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_SUGGESTED_DISH_CATEGORY,
			payload: {
				selectedSuggestedDishCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_SUGGESTED_DISH_SUB_CATEGORIES,
			payload: {
				fetchedSuggestedDishSubCategories: value,
			},
		});
	}, []);

	const setSuggestedDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_SUGGESTED_DISH_SUB_CATEGORY,
			payload: {
				selectedSuggestedDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedSuggestedDishes.includes(el.id));
			const filteredFetchedSuggestedDishes = state.fetchedSuggestedDishes.filter((el) =>
				state.selectedSuggestedDishes.includes(el.id),
			);
			dispatch({
				type: EditDishActionTypes.SET_FETCHED_SUGGESTED_DISHES,
				payload: {
					fetchedSuggestedDishes: [...filteredValue, ...filteredFetchedSuggestedDishes],
				},
			});
		},
		[state.selectedSuggestedDishes, state.fetchedSuggestedDishes],
	);

	const setSuggestedDishes = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_SUGGESTED_DISHES,
			payload: {
				selectedSuggestedDishes: value,
			},
		});
	}, []);

	const removeSuggestedDish = useCallback(
		(value: string) => {
			const updatedSelectedSuggestedDishes = state.selectedSuggestedDishes.filter((el) => el !== value);
			dispatch({
				type: EditDishActionTypes.REMOVE_SUGGESTED_DISH,
				payload: {
					selectedSuggestedDishes: updatedSelectedSuggestedDishes,
				},
			});
		},
		[state.selectedSuggestedDishes],
	);

	const setAdditionalDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_ADDITIONAL_DISH_CATEGORY,
			payload: {
				selectedAdditionalDishCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_ADDITIONAL_DISH_SUB_CATEGORIES,
			payload: {
				fetchedAdditionalDishSubCategories: value,
			},
		});
	}, []);

	const setAdditionalDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_ADDITIONAL_DISH_SUB_CATEGORY,
			payload: {
				selectedAdditionalDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedAdditionalDishes.includes(el.id));
			const filteredFetchedAdditionalDishes = state.fetchedAdditionalDishes.filter((el) =>
				state.selectedAdditionalDishes.includes(el.id),
			);
			dispatch({
				type: EditDishActionTypes.SET_FETCHED_ADDITIONAL_DISHES,
				payload: {
					fetchedAdditionalDishes: [...filteredValue, ...filteredFetchedAdditionalDishes],
				},
			});
		},
		[state.selectedAdditionalDishes, state.fetchedAdditionalDishes],
	);

	const setAdditionalDishes = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_ADDITIONAL_DISHES,
			payload: {
				selectedAdditionalDishes: value,
			},
		});
	}, []);

	const removeAdditionalDish = useCallback(
		(value: string) => {
			const updatedSelectedAdditionalDishes = state.selectedAdditionalDishes.filter((el) => el !== value);
			dispatch({
				type: EditDishActionTypes.REMOVE_ADDITIONAL_DISH,
				payload: {
					selectedAdditionalDishes: updatedSelectedAdditionalDishes,
				},
			});
		},
		[state.selectedAdditionalDishes],
	);

	const setDishModificationCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_DISH_MODIFICATION_CATEGORY,
			payload: {
				selectedDishModificationCategory: value,
			},
		});
	}, []);

	const setFetchedDishModificationSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDishActionTypes.SET_FETCHED_DISH_MODIFICATION_SUB_CATEGORIES,
			payload: {
				fetchedDishModificationSubCategories: value,
			},
		});
	}, []);

	const setDishModificationSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditDishActionTypes.SET_DISH_MODIFICATION_SUB_CATEGORY,
			payload: {
				selectedDishModificationSubCategory: value,
			},
		});
	}, []);

	const setFetchedDishModifications = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedDishModifications.includes(el.id));
			const filteredFetchedDishModifications = state.fetchedDishModifications.filter((el) =>
				state.selectedDishModifications.includes(el.id),
			);
			dispatch({
				type: EditDishActionTypes.SET_FETCHED_DISH_MODIFICATIONS,
				payload: {
					fetchedDishModifications: [...filteredValue, ...filteredFetchedDishModifications],
				},
			});
		},
		[state.selectedDishModifications, state.fetchedDishModifications],
	);

	const setDishModifications = useCallback((value: string[]) => {
		dispatch({
			type: EditDishActionTypes.SET_DISH_MODIFICATIONS,
			payload: {
				selectedDishModifications: value,
			},
		});
	}, []);

	const removeDishModification = useCallback(
		(value: string) => {
			const updatedSelectedDishModifications = state.selectedDishModifications.filter((el) => el !== value);
			const copyIDsShowInComboDishes = { ...state.IDsShowInComboDishes };
			delete copyIDsShowInComboDishes[value];
			dispatch({
				type: EditDishActionTypes.REMOVE_DISH_MODIFICATION,
				payload: {
					selectedDishModifications: updatedSelectedDishModifications,
					IDsShowInComboDishes: copyIDsShowInComboDishes,
				},
			});
		},
		[state.selectedDishModifications, state.IDsShowInComboDishes],
	);

	const setIDsShowInComboDishes = useCallback((value: IIDsShowInComboDishes) => {
		dispatch({
			type: EditDishActionTypes.SET_IDS_SHOW_IN_COMBO_DISHES,
			payload: {
				IDsShowInComboDishes: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: EditDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const toggleTaxPercentProtectedFromSync = useCallback(
		(value: boolean) => {
			dispatch({
				type: EditDishActionTypes.TOGGLE_APPLICABLE_FOR_VAT,
				payload: {
					isTaxPercentProtectedFromSync: value,
					taxPercent: value ? state.taxPercent : null,
				},
			});
		},
		[state.taxPercent],
	);

	const changeTaxPercent = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_TAX_PERCENT,
			payload: {
				taxPercent: value,
			},
		});
	}, []);

	const toggleOpenPrice = useCallback(
		(value: boolean) => {
			dispatch({
				type: EditDishActionTypes.TOGGLE_OPEN_PRICE,
				payload: {
					hasOpenPrice: value,
					openPrice: value ? state.openPrice : null,
					openPriceMaxThreshold: value ? state.openPriceMaxThreshold : null,
				},
			});
		},
		[state.openPrice, state.openPriceMaxThreshold],
	);

	const changeOpenPrice = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_OPEN_PRICE,
			payload: {
				openPrice: value,
			},
		});
	}, []);

	const changeOpenPriceMaxThreshold = useCallback((value: Nullable<number>) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_OPEN_PRICE_MAX_THRESHOLD,
			payload: {
				openPriceMaxThreshold: value,
			},
		});
	}, []);

	const toggleFiscalized = useCallback(() => {
		dispatch({
			type: EditDishActionTypes.TOGGLE_FISCALIZED,
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditDishModalWindow) => {
		dispatch({
			type: EditDishActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IEditDishErrorResponseData | null) => {
		dispatch({
			type: EditDishActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditDishActionTypes.RESET_FIELDS }), []);

	return (
		<EditDishContext.Provider
			value={{
				refetchData,
				changeVisibleForm,
				setFileId,
				deletePhoto,
				setSmallFileId,
				deleteSmallPhoto,
				setVideoFileId,
				deleteVideo,
				setIsVideoActive,
				changeDishName,
				setFetchedRestaurants,
				setRestaurantsForPrice,
				changePrice,
				addPrices,
				removePrice,
				setRestaurantWithTurnOffComment,
				setRestaurantWithRequiredComment,
				changeShortDescription,
				setFetchedComboModifier,
				setComboModifier,
				updateComboModifier,
				setDisabledInRestaurants,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedModifierGroups,
				setModifierGroups,
				changeLowerLimit,
				changeUpperLimit,
				toggleLimitsProtectedFromSync,
				setModifierGroupsAsAdditional,
				setFetchedCategories,
				setSuggestedDishCategory,
				setFetchedSuggestedDishSubCategories,
				setSuggestedDishSubCategory,
				setFetchedSuggestedDishes,
				setSuggestedDishes,
				removeSuggestedDish,
				setAdditionalDishCategory,
				setFetchedAdditionalDishSubCategories,
				setAdditionalDishSubCategory,
				setFetchedAdditionalDishes,
				setAdditionalDishes,
				removeAdditionalDish,
				setDishModificationCategory,
				setFetchedDishModificationSubCategories,
				setDishModificationSubCategory,
				setFetchedDishModifications,
				setDishModifications,
				removeDishModification,
				setIDsShowInComboDishes,
				changeExternalId,
				generateUniqueExternalId,
				toggleTaxPercentProtectedFromSync,
				changeTaxPercent,
				toggleOpenPrice,
				changeOpenPrice,
				changeOpenPriceMaxThreshold,
				toggleFiscalized,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				visibleForm: state.visibleForm,
				dishId: state.dishId,
				filePath: state.filePath,
				preSignedUrl: state.preSignedUrl,
				fileId: state.fileId,
				smallFilePath: state.smallFilePath,
				smallPreSignedUrl: state.smallPreSignedUrl,
				smallFileId: state.smallFileId,
				videoPath: state.videoPath,
				videoPreSignedUrl: state.videoPreSignedUrl,
				videoFileId: state.videoFileId,
				isVideoActive: state.isVideoActive,
				name: state.name,
				selectedCustomer: state.selectedCustomer,
				articleNumber: state.articleNumber,
				fetchedRestaurants: state.fetchedRestaurants,
				restaurantsForPrice: state.restaurantsForPrice,
				price: state.price,
				totalPrices: state.totalPrices,
				restaurantWithTurnOffComment: state.restaurantWithTurnOffComment,
				restaurantWithRequiredComment: state.restaurantWithRequiredComment,
				shortDescription: state.shortDescription,
				posName: state.posName,
				isComboDish: state.isComboDish,
				fetchedComboModifier: state.fetchedComboModifier,
				initialSelectedComboModifier: state.initialSelectedComboModifier,
				selectedComboModifier: state.selectedComboModifier,
				initialCategories: state.initialCategories,
				initialSubCategories: state.initialSubCategories,
				externalId: state.externalId,
				isCreatedInPos: state.isCreatedInPos,
				isDisabledInRestaurants: state.isDisabledInRestaurants,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				fetchedModifierGroups: state.fetchedModifierGroups,
				initialModifierGroups: state.initialModifierGroups,
				selectedModifierGroups: state.selectedModifierGroups,
				modifierGroupsWithAdditionalData: state.modifierGroupsWithAdditionalData,
				initialModifierGroupsAsAdditional: state.initialModifierGroupsAsAdditional,
				selectedModifierGroupsAsAdditional: state.selectedModifierGroupsAsAdditional,
				fetchedCategories: state.fetchedCategories,
				selectedSuggestedDishCategory: state.selectedSuggestedDishCategory,
				fetchedSuggestedDishSubCategories: state.fetchedSuggestedDishSubCategories,
				selectedSuggestedDishSubCategory: state.selectedSuggestedDishSubCategory,
				fetchedSuggestedDishes: state.fetchedSuggestedDishes,
				selectedSuggestedDishes: state.selectedSuggestedDishes,
				selectedAdditionalDishCategory: state.selectedAdditionalDishCategory,
				fetchedAdditionalDishSubCategories: state.fetchedAdditionalDishSubCategories,
				selectedAdditionalDishSubCategory: state.selectedAdditionalDishSubCategory,
				fetchedAdditionalDishes: state.fetchedAdditionalDishes,
				selectedAdditionalDishes: state.selectedAdditionalDishes,
				selectedDishModificationCategory: state.selectedDishModificationCategory,
				fetchedDishModificationSubCategories: state.fetchedDishModificationSubCategories,
				selectedDishModificationSubCategory: state.selectedDishModificationSubCategory,
				fetchedDishModifications: state.fetchedDishModifications,
				selectedDishModifications: state.selectedDishModifications,
				IDsShowInComboDishes: state.IDsShowInComboDishes,
				isTaxPercentProtectedFromSync: state.isTaxPercentProtectedFromSync,
				taxPercent: state.taxPercent,
				hasOpenPrice: state.hasOpenPrice,
				openPrice: state.openPrice,
				openPriceMaxThreshold: state.openPriceMaxThreshold,
				isFiscalized: state.isFiscalized,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditDishContext.Provider>
	);
};

export default EditDishState;
